import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';

const TYPE_OPTIONS = ['AI', 'Automation', 'Utility', 'Platform Services', 'SaaS Ops', 'Data', 'Security', 'DevOps'];
const TEAM_OPTIONS = ['SaaS Ops', 'Platform Services', 'Creative', 'Finance', 'Media', 'DX', 'COE'];

// ── Card Preview ──────────────────────────────────────────────────────────────

interface CardPreviewProps {
  title: string;
  description: string;
  type: string;
  team: string;
  externalLink: string;
  docsLink: string;
  items: string[];
}

const CardPreview: React.FC<CardPreviewProps> = ({ title, description, type, team, externalLink, docsLink, items }) => (
  <Box sx={{
    border: '1px solid rgba(25,118,210,0.2)',
    borderRadius: '12px',
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    bgcolor: 'white',
    p: 2,
    width: 260,
    flexShrink: 0,
  }}>
    {externalLink && (
      <Tooltip title="External link">
        <LaunchIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color: 'primary.main', cursor: 'pointer' }} />
      </Tooltip>
    )}

    {/* Type + Team badges */}
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
      {type && (
        <Box sx={{ fontSize: '0.72rem', color: 'primary.main', bgcolor: 'rgba(25,118,210,0.05)', border: '1px solid rgba(25,118,210,0.2)', px: 1, py: 0.3, borderRadius: '16px' }}>
          {type}
        </Box>
      )}
      {team && (
        <Box sx={{ fontSize: '0.72rem', color: '#6b7280', bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', px: 1, py: 0.3, borderRadius: '16px' }}>
          {team}
        </Box>
      )}
    </Box>

    {/* Title */}
    <Box sx={{ borderBottom: '1px solid rgba(25,118,210,0.1)', pb: 1, mb: 1 }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
        {title || 'Card Title'}
      </Typography>
    </Box>

    {/* Description + items */}
    <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.4 }}>
        {description || 'Card description will appear here.'}
      </Typography>
      {items.filter(Boolean).map((item, i) => (
        <Typography key={i} sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5, '&:before': { content: '"• "' } }}>
          {item}
        </Typography>
      ))}
    </Box>

    {/* Explore button */}
    <Button
      fullWidth
      variant="contained"
      size="small"
      disabled={!docsLink && !externalLink}
      sx={{ bgcolor: 'primary.main', color: 'white', textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem', '&:hover': { bgcolor: '#162d5c' } }}
    >
      Explore
    </Button>
  </Box>
);

// ── YAML builder ──────────────────────────────────────────────────────────────

function buildYaml(
  title: string,
  description: string,
  type: string,
  team: string,
  externalLink: string,
  docsLink: string,
  items: string[],
): string {
  const lines: string[] = [`- title: "${title || 'My Product'}"`];
  if (description) lines.push(`  description: "${description}"`);
  if (type) lines.push(`  type: "${type}"`);
  if (team) lines.push(`  team: "${team}"`);
  const filteredItems = items.filter(Boolean);
  if (filteredItems.length) {
    lines.push('  items:');
    filteredItems.forEach(i => lines.push(`    - "${i}"`));
  }
  const hasLinks = externalLink || docsLink;
  if (hasLinks) {
    lines.push('  links:');
    if (externalLink) lines.push(`    external: "${externalLink}"`);
    if (docsLink) lines.push(`    docs: "${docsLink}"`);
  }
  return lines.join('\n');
}

// ── Main component ────────────────────────────────────────────────────────────

export const CardPlayground: React.FC = () => {
  const [title, setTitle] = useState('ACT-AMS');
  const [description, setDescription] = useState('A centralized monitoring tool for managing and tracking audience activations within the AMS workflow.');
  const [type, setType] = useState('Automation');
  const [team, setTeam] = useState('SaaS Ops');
  const [externalLink, setExternalLink] = useState('https://act-ams.epsilon.com/');
  const [docsLink, setDocsLink] = useState('/marketplace/docs/default/component/act-ams');
  const [items, setItems] = useState<string[]>(['Provides real-time visibility into audience activation statuses', '']);
  const [copied, setCopied] = useState(false);

  const yaml = buildYaml(title, description, type, team, externalLink, docsLink, items);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(yaml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [yaml]);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left: form */}
      <Box sx={{ width: '45%', flexShrink: 0, overflowY: 'auto', p: 3, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Asset Fields
        </Typography>

        <TextField size="small" label="Title *" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
        <TextField size="small" label="Description *" value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline rows={2} />

        <FormControl size="small" fullWidth>
          <InputLabel>Type</InputLabel>
          <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
            {TYPE_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Team</InputLabel>
          <Select value={team} label="Team" onChange={e => setTeam(e.target.value)}>
            {TEAM_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" label="External Link" value={externalLink} onChange={e => setExternalLink(e.target.value)} fullWidth placeholder="https://..." />
        <TextField size="small" label="Docs Link" value={docsLink} onChange={e => setDocsLink(e.target.value)} fullWidth placeholder="/marketplace/docs/default/component/..." />

        {/* Items */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>Bullet Items (optional)</Typography>
            <IconButton size="small" onClick={() => setItems(it => [...it, ''])}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          {items.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Feature or highlight"
                value={item}
                onChange={e => setItems(it => it.map((v, idx) => idx === i ? e.target.value : v))}
              />
              {items.length > 1 && (
                <IconButton size="small" onClick={() => setItems(it => it.filter((_, idx) => idx !== i))}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right: preview + yaml */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Card preview */}
        <Box sx={{
          flex: '0 0 55%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff, #faf5ff)',
          p: 3,
          overflow: 'auto',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Card Preview
            </Typography>
            <CardPreview
              title={title}
              description={description}
              type={type}
              team={team}
              externalLink={externalLink}
              docsLink={docsLink}
              items={items}
            />
          </Box>
        </Box>

        {/* YAML output */}
        <Box sx={{ borderTop: '1px solid #e5e7eb', bgcolor: '#1e1e1e', flex: '0 0 45%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              apps.yaml entry
            </Typography>
            <Button
              size="small"
              onClick={handleCopy}
              sx={{ color: copied ? '#4ade80' : '#9ca3af', textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <pre style={{ margin: 0, fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.78rem', color: '#ce9178', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {yaml}
            </pre>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
